import type { CoverageLayer } from '../../../core/folderCoverage/types'
import type {
  KnowledgeChangeSet,
  KnowledgeRun,
} from '../../compass/models/KnowledgeIngestion'
import type {
  PersistedGraph,
  PersistedNode,
} from '../../compass/models/KnowledgeGraph'
import type { NetworkReviewBaselineResult } from '../models/ReviewBaseline'
import { fingerprintGraphResources } from './NetworkReviewBaselineStateService'

export type NetworkReviewAngle =
  | 'security'
  | 'cost'
  | 'optimization'
  | 'drift'
  | 'compliance'

export type NetworkReviewSeverity =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'info'

export interface NetworkReviewItem {
  id: string
  angle: NetworkReviewAngle
  severity: NetworkReviewSeverity
  title: string
  summary: string
  whyItMatters: string
  recommendedAction: string
  sourceLabel: string
  resourceIds: string[]
  focusPresetId?: string
  focusLabel?: string
  zoom?: 2 | 3 | 4
  panel?: 'map' | 'review' | 'scenarios'
}

export interface NetworkReviewSummary {
  total: number
  critical: number
  high: number
  medium: number
  low: number
  info: number
  byAngle: Record<NetworkReviewAngle, number>
}

export interface BuildNetworkReviewItemsInput {
  graph?: Pick<PersistedGraph, 'nodes'> | null
  baseline?: NetworkReviewBaselineResult | null
  coverageGaps: readonly CoverageLayer[]
  recentRuns: readonly KnowledgeRun[]
  recentChangeSets: readonly KnowledgeChangeSet[]
}

const EMPTY_ANGLE_COUNTS: Record<NetworkReviewAngle, number> = {
  security: 0,
  cost: 0,
  optimization: 0,
  drift: 0,
  compliance: 0,
}

const SEVERITY_ORDER: Record<NetworkReviewSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
}

export function buildNetworkReviewItems({
  graph,
  baseline,
  coverageGaps,
  recentRuns,
  recentChangeSets,
}: BuildNetworkReviewItemsInput): NetworkReviewItem[] {
  const nodes = graph?.nodes ?? []
  const resources = nodes.filter(isReviewableResource)
  const items: NetworkReviewItem[] = []

  items.push(...itemsFromReviewSignals(nodes))

  const publicResources = resources.filter((node) => {
    const exposure = publicExposureLevel(node)
    return exposure === 'open' || exposure === 'restricted'
  })
  const publicDataStores = publicResources.filter(isDataStore)
  if (publicResources.length > 0) {
    items.push({
      id: 'security.public-exposure',
      angle: 'security',
      severity: publicDataStores.length > 0 ? 'critical' : 'high',
      title: publicDataStores.length > 0
        ? 'Public exposure reaches data stores'
        : 'Publicly reachable resources detected',
      summary: publicDataStores.length > 0
        ? `${publicDataStores.length} data store${plural(publicDataStores.length)} and ${publicResources.length} total resource${plural(publicResources.length)} have public exposure signals.`
        : `${publicResources.length} resource${plural(publicResources.length)} have public exposure signals.`,
      whyItMatters: publicDataStores.length > 0
        ? 'Publicly reachable data tiers are usually the fastest path from architecture review to security remediation.'
        : 'Public exposure expands the attack surface and should be reviewed with routing, WAF, and ownership context.',
      recommendedAction: publicDataStores.length > 0
        ? 'Inspect the public path to data, confirm whether the exposure is intentional, and attach remediation or acceptance evidence.'
        : 'Inspect the attack-surface view and confirm whether each public entry point has the expected controls.',
      sourceLabel: 'Folder graph',
      resourceIds: publicResources.map((node) => node.id),
      focusPresetId: publicDataStores.length > 0 ? 'public-path-to-data' : 'attack-surface',
      focusLabel: publicDataStores.length > 0 ? 'Public Path to Data' : 'Attack Surface',
      zoom: publicDataStores.length > 0 ? 4 : 3,
      panel: 'map',
    })
  }

  const unencryptedData = resources.filter((node) => isDataStore(node) && encryptionAtRest(node) === false)
  if (unencryptedData.length > 0) {
    items.push({
      id: 'security.unencrypted-data',
      angle: 'security',
      severity: 'critical',
      title: 'Unencrypted data stores',
      summary: `${unencryptedData.length} data store${plural(unencryptedData.length)} explicitly report encryption at rest as disabled.`,
      whyItMatters: 'Data stores without encryption evidence create audit and breach-impact risk even when network access is restricted.',
      recommendedAction: 'Inspect the affected data stores, confirm source freshness, then enable encryption or record accepted-risk evidence.',
      sourceLabel: 'Folder graph',
      resourceIds: unencryptedData.map((node) => node.id),
      focusPresetId: 'attack-surface',
      focusLabel: 'Attack Surface',
      zoom: 4,
      panel: 'map',
    })
  }

  const highSpend = resources.filter((node) => monthlySpend(node) >= 500)
  if (highSpend.length > 0) {
    const total = highSpend.reduce((sum, node) => sum + monthlySpend(node), 0)
    items.push({
      id: 'cost.high-spend',
      angle: 'cost',
      severity: total >= 5000 || highSpend.some((node) => monthlySpend(node) >= 1000)
        ? 'high'
        : 'medium',
      title: 'High monthly spend concentration',
      summary: `${highSpend.length} resource${plural(highSpend.length)} account for ${formatUsd(total)} per month in measured spend.`,
      whyItMatters: 'Concentrated spend is where ownership, tagging, and utilization gaps become expensive fastest.',
      recommendedAction: 'Open the cost-waste view, validate owner and cost-center coverage, and assign right-sizing review to the owning team.',
      sourceLabel: 'Cost / utilization source',
      resourceIds: highSpend.map((node) => node.id),
      focusPresetId: 'cost-waste',
      focusLabel: 'Cost Waste',
      zoom: 4,
      panel: 'map',
    })
  }

  const missingCostCenter = resources.filter((node) => monthlySpend(node) > 10 && !hasCostCenter(node))
  if (missingCostCenter.length > 0) {
    items.push({
      id: 'cost.missing-cost-center',
      angle: 'cost',
      severity: 'medium',
      title: 'Spend missing cost center',
      summary: `${missingCostCenter.length} billed resource${plural(missingCostCenter.length)} lack a cost-center tag or field.`,
      whyItMatters: 'Unattributed spend cannot be routed to the right team, budget, or optimization workflow.',
      recommendedAction: 'Review ownership gaps, add cost-center metadata at source, and refresh the folder source run.',
      sourceLabel: 'Tags / cost coverage',
      resourceIds: missingCostCenter.map((node) => node.id),
      focusPresetId: 'ownership-gaps',
      focusLabel: 'Ownership Gaps',
      zoom: 4,
      panel: 'map',
    })
  }

  const idleSpend = resources.filter((node) => monthlySpend(node) > 0 && isIdle(node))
  if (idleSpend.length > 0) {
    const total = idleSpend.reduce((sum, node) => sum + monthlySpend(node), 0)
    items.push({
      id: 'optimization.idle-spend',
      angle: 'optimization',
      severity: total >= 500 ? 'high' : 'medium',
      title: 'Idle capacity still billing',
      summary: `${idleSpend.length} billed resource${plural(idleSpend.length)} look stopped, idle, or near-zero utilization (${formatUsd(total)} per month measured).`,
      whyItMatters: 'Idle but billed resources are strong cleanup candidates, especially when they are also public or unowned.',
      recommendedAction: 'Open the cost-waste view, confirm utilization source freshness, then create a cleanup or right-sizing task.',
      sourceLabel: 'Cost / utilization source',
      resourceIds: idleSpend.map((node) => node.id),
      focusPresetId: 'cost-waste',
      focusLabel: 'Cost Waste',
      zoom: 4,
      panel: 'map',
    })
  }

  const staleResources = resources.filter(isStale)
  if (staleResources.length > 0) {
    items.push({
      id: 'optimization.stale-resources',
      angle: 'optimization',
      severity: 'low',
      title: 'Stale resources',
      summary: `${staleResources.length} resource${plural(staleResources.length)} have not changed in more than 90 days.`,
      whyItMatters: 'Long-untouched resources often indicate abandoned infrastructure, missing ownership, or forgotten dependencies.',
      recommendedAction: 'Inspect the stale resources, check owner and dependency context, then mark keep/remove decisions in the review.',
      sourceLabel: 'Folder graph',
      resourceIds: staleResources.map((node) => node.id),
      focusPresetId: 'cost-waste',
      focusLabel: 'Cost Waste',
      zoom: 4,
      panel: 'map',
    })
  }

  const manualResources = resources.filter(isManualOrUnmanaged)
  if (manualResources.length > 0) {
    items.push({
      id: 'drift.manual-resources',
      angle: 'drift',
      severity: manualResources.length >= 5 ? 'high' : 'medium',
      title: 'Resources outside IaC governance',
      summary: `${manualResources.length} resource${plural(manualResources.length)} look manual, unmanaged, or drifted from declared infrastructure.`,
      whyItMatters: 'Manual or unmanaged production resources are the core drift signal between accepted architecture and runtime reality.',
      recommendedAction: 'Open the drift view, compare against the latest IaC source, then import, remove, or document the exception.',
      sourceLabel: 'IaC / baseline coverage',
      resourceIds: manualResources.map((node) => node.id),
      focusPresetId: 'drift-from-baseline',
      focusLabel: 'Drift from Baseline',
      zoom: 4,
      panel: 'map',
    })
  }

  items.push(...itemsFromBaselineDelta(graph, baseline))

  const proposedChanges = recentChangeSets.filter((changeSet) => changeSet.status === 'proposed')
  if (proposedChanges.length > 0) {
    items.push({
      id: 'drift.proposed-changes',
      angle: 'drift',
      severity: 'medium',
      title: 'Proposed changes awaiting review',
      summary: `${proposedChanges.length} proposed change set${plural(proposedChanges.length)} can be reviewed as plan overlays before accepting the current graph.`,
      whyItMatters: 'Plan overlays let the team catch new exposure, drift, and ownership impact before deployment changes the live graph.',
      recommendedAction: 'Open Plan Review, preview the IaC diff against the full folder graph, and generate the change-review note.',
      sourceLabel: 'Plan overlay',
      resourceIds: [],
      focusPresetId: 'planned-changes',
      focusLabel: 'Planned Changes',
      zoom: 4,
      panel: 'scenarios',
    })
  }

  const missingOwners = resources.filter((node) => !hasOwner(node))
  if (missingOwners.length > 0) {
    items.push({
      id: 'compliance.missing-owner',
      angle: 'compliance',
      severity: missingOwners.length >= 20 ? 'high' : 'medium',
      title: 'Resources missing ownership',
      summary: `${missingOwners.length} resource${plural(missingOwners.length)} have no owner, team, service, or workload signal.`,
      whyItMatters: 'Ownership gaps block incident response, cost allocation, evidence collection, and review accountability.',
      recommendedAction: 'Open ownership gaps, enrich owner/workload tags at the source, then rerun or refresh the source.',
      sourceLabel: 'Tags / ownership coverage',
      resourceIds: missingOwners.map((node) => node.id),
      focusPresetId: 'ownership-gaps',
      focusLabel: 'Ownership Gaps',
      zoom: 4,
      panel: 'map',
    })
  }

  const configViolations = resources.filter(hasConfigViolation)
  if (configViolations.length > 0) {
    items.push({
      id: 'compliance.config-violations',
      angle: 'compliance',
      severity: configViolations.some((node) => criticalConfigViolation(node)) ? 'critical' : 'medium',
      title: 'Provider policy violations',
      summary: `${configViolations.length} resource${plural(configViolations.length)} have non-compliant provider policy or config-rule evidence.`,
      whyItMatters: 'Provider policy failures are direct compliance evidence and should be tied to controls, remediation, or accepted risk.',
      recommendedAction: 'Open review findings, inspect config-rule evidence, and link the remediation to the affected control or resource.',
      sourceLabel: 'Config / policy source',
      resourceIds: configViolations.map((node) => node.id),
      focusPresetId: 'ownership-gaps',
      focusLabel: 'Ownership Gaps',
      zoom: 4,
      panel: 'review',
    })
  }

  for (const gap of coverageGaps) {
    items.push(itemFromCoverageGap(gap))
  }

  const failedRuns = recentRuns.filter((run) => run.status === 'failed')
  if (failedRuns.length > 0) {
    items.push({
      id: 'drift.failed-runs',
      angle: 'drift',
      severity: 'medium',
      title: 'Source runs failed',
      summary: `${failedRuns.length} source run${plural(failedRuns.length)} failed, so the current review may be stale or incomplete.`,
      whyItMatters: 'A failed source run means the review queue may be missing recent changes or source fidelity evidence.',
      recommendedAction: 'Open Sources, inspect the failed run, fix the connector or uploaded source, and rerun ingestion.',
      sourceLabel: 'Source activity',
      resourceIds: [],
      panel: 'review',
    })
  }

  return dedupeById(items).sort((a, b) => {
    const severityDelta = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
    if (severityDelta !== 0) return severityDelta
    return a.angle.localeCompare(b.angle) || a.title.localeCompare(b.title)
  })
}

export function summarizeNetworkReviewItems(items: readonly NetworkReviewItem[]): NetworkReviewSummary {
  const byAngle = { ...EMPTY_ANGLE_COUNTS }
  const summary: NetworkReviewSummary = {
    total: items.length,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    info: 0,
    byAngle,
  }
  for (const item of items) {
    summary[item.severity] += 1
    byAngle[item.angle] += 1
  }
  return summary
}

function itemsFromBaselineDelta(
  graph: Pick<PersistedGraph, 'nodes'> | null | undefined,
  baseline: NetworkReviewBaselineResult | null | undefined,
): NetworkReviewItem[] {
  const summary = baseline?.summary
  if (!graph || !summary?.hasBaseline) return []

  const currentResources = fingerprintGraphResources(graph)
  const nodeIdsForKeys = (keys: readonly string[]): string[] => keys
    .map((key) => currentResources[key]?.nodeId)
    .filter((nodeId): nodeId is string => Boolean(nodeId))

  const items: NetworkReviewItem[] = []
  if (summary.addedCount > 0) {
    items.push({
      id: 'drift.baseline-added-resources',
      angle: 'drift',
      severity: summary.addedCount >= 10 ? 'high' : 'medium',
      title: 'Resources added since baseline',
      summary: `${summary.addedCount} resource${plural(summary.addedCount)} appear in the current graph but not in the accepted baseline.`,
      whyItMatters: 'New runtime resources need ownership, exposure, cost, and IaC checks before the team accepts the current state as the new baseline.',
      recommendedAction: 'Open Drift from Baseline, inspect the added resources, then either link them to a planned change or record why the baseline should move.',
      sourceLabel: 'Accepted baseline',
      resourceIds: nodeIdsForKeys(summary.addedResourceKeys),
      focusPresetId: 'drift-from-baseline',
      focusLabel: 'Drift from Baseline',
      zoom: 4,
      panel: 'map',
    })
  }

  if (summary.changedCount > 0) {
    items.push({
      id: 'drift.baseline-changed-resources',
      angle: 'drift',
      severity: summary.changedCount >= 10 ? 'high' : 'medium',
      title: 'Resources changed since baseline',
      summary: `${summary.changedCount} resource${plural(summary.changedCount)} have identity-stable fingerprints that differ from the accepted baseline.`,
      whyItMatters: 'Changed configuration can introduce exposure, ownership, compliance, or cost drift even when the resource identity stays the same.',
      recommendedAction: 'Open Drift from Baseline, compare the changed resources against source evidence, and route unexpected changes into remediation or acceptance.',
      sourceLabel: 'Accepted baseline',
      resourceIds: nodeIdsForKeys(summary.changedResourceKeys),
      focusPresetId: 'drift-from-baseline',
      focusLabel: 'Drift from Baseline',
      zoom: 4,
      panel: 'map',
    })
  }

  if (summary.removedCount > 0) {
    items.push({
      id: 'drift.baseline-removed-resources',
      angle: 'drift',
      severity: 'medium',
      title: 'Baseline resources missing from current graph',
      summary: `${summary.removedCount} resource${plural(summary.removedCount)} from the accepted baseline are absent from the current graph.`,
      whyItMatters: 'A missing resource can be an intentional retirement, a failed source run, or an inventory gap that hides dependency and risk context.',
      recommendedAction: 'Check source freshness and the removal evidence before accepting the current baseline.',
      sourceLabel: 'Accepted baseline',
      resourceIds: [],
      focusPresetId: 'drift-from-baseline',
      focusLabel: 'Drift from Baseline',
      zoom: 4,
      panel: 'review',
    })
  }

  return items
}

function itemsFromReviewSignals(nodes: readonly PersistedNode[]): NetworkReviewItem[] {
  return nodes
    .filter(isReviewSignalNode)
    .map((node): NetworkReviewItem => {
      const props = propsOf(node)
      const angle = angleFromSignal(stringProp(props, ['angle']))
      const severity = severityFromSignal(stringProp(props, ['severity']))
      const affected = arrayOfStrings(props.affectedResourceKeys)
      const scannerId = stringProp(props, ['scannerId']) ?? 'AWS hosted scanner'
      const remediation = stringProp(props, ['remediation'])
      return {
        id: `signal.${node.id}`,
        angle,
        severity,
        title: stringProp(props, ['title']) ?? node.label ?? 'Review signal',
        summary: stringProp(props, ['summary']) ?? 'A hosted scanner produced a review signal for this folder.',
        whyItMatters: whySignalMatters(angle),
        recommendedAction: remediation ?? defaultSignalAction(angle),
        sourceLabel: scannerId,
        resourceIds: affected,
        focusPresetId: focusPresetForAngle(angle),
        focusLabel: focusLabelForAngle(angle),
        zoom: affected.length > 0 ? 4 : 3,
        panel: affected.length > 0 ? 'map' : 'review',
      }
    })
}

function isReviewSignalNode(node: PersistedNode): boolean {
  const props = propsOf(node)
  if (props.source === 'aws-hosted-sync' || stringProp(props, ['scannerId'])) return true
  return Array.isArray(node.tags) && node.tags.includes('review-signal')
}

function angleFromSignal(value: string | null): NetworkReviewAngle {
  switch (value) {
    case 'cost':
    case 'optimization':
    case 'drift':
    case 'compliance':
    case 'security':
      return value
    case 'identity':
      return 'security'
    case 'observability':
      return 'compliance'
    default:
      return 'compliance'
  }
}

function severityFromSignal(value: string | null): NetworkReviewSeverity {
  switch (value) {
    case 'critical':
    case 'high':
    case 'medium':
    case 'low':
    case 'info':
      return value
    default:
      return 'medium'
  }
}

function whySignalMatters(angle: NetworkReviewAngle): string {
  switch (angle) {
    case 'security':
      return 'Hosted scanner findings carry runtime context from the connected cloud account, so they should be reviewed before accepting the folder state.'
    case 'cost':
      return 'Cost signals may be account/service/tag rollups before they are resource-level joins. Treat rollup spend as ownership and investigation work, and reserve resource-level action for costs tied to stable tags or resource keys.'
    case 'optimization':
      return 'Optimization signals highlight runtime waste or cleanup candidates that static diagrams rarely expose on their own.'
    case 'drift':
      return 'Drift signals show where live cloud state may have moved away from the accepted architecture or source baseline.'
    case 'compliance':
      return 'Compliance signals need disposition because they become audit evidence, remediation work, or accepted risk.'
  }
}

function defaultSignalAction(angle: NetworkReviewAngle): string {
  switch (angle) {
    case 'cost':
    case 'optimization':
      return 'Open the cost and optimization view, verify ownership, and route the item to the responsible team.'
    case 'drift':
      return 'Compare the signal against the accepted baseline and decide whether to remediate, document, or accept the change.'
    case 'security':
      return 'Inspect the affected resources, confirm whether the exposure or identity relationship is intended, and attach remediation evidence.'
    case 'compliance':
      return 'Review the finding evidence, link it to the relevant control or resource, and record the remediation decision.'
  }
}

function focusPresetForAngle(angle: NetworkReviewAngle): string {
  switch (angle) {
    case 'cost':
    case 'optimization':
      return 'cost-waste'
    case 'drift':
      return 'drift-from-baseline'
    case 'security':
      return 'attack-surface'
    case 'compliance':
      return 'ownership-gaps'
  }
}

function focusLabelForAngle(angle: NetworkReviewAngle): string {
  switch (angle) {
    case 'cost':
      return 'Cost Waste'
    case 'optimization':
      return 'Cost Waste'
    case 'drift':
      return 'Drift from Baseline'
    case 'security':
      return 'Attack Surface'
    case 'compliance':
      return 'Ownership Gaps'
  }
}

function itemFromCoverageGap(gap: CoverageLayer): NetworkReviewItem {
  const angle = angleFromCoverageLayer(gap.id)
  return {
    id: `coverage.${gap.id}`,
    angle,
    severity: gap.ratio === 0 ? 'medium' : 'low',
    title: `${gap.label} coverage gap`,
    summary: `${gap.filled} of ${gap.total} measured resources carry ${gap.label.toLowerCase()} coverage.`,
    whyItMatters: 'Coverage gaps reduce confidence in automated review output because some resources lack the source fields needed for this angle.',
    recommendedAction: 'Open Sources, add or refresh the source that provides this layer, then rerun the review.',
    sourceLabel: 'Source fidelity',
    resourceIds: [],
    focusPresetId: angle === 'cost'
      ? 'cost-waste'
      : angle === 'security'
        ? 'attack-surface'
        : angle === 'drift'
          ? 'drift-from-baseline'
          : 'ownership-gaps',
    focusLabel: gap.label,
    zoom: 4,
    panel: 'review',
  }
}

function angleFromCoverageLayer(id: string): NetworkReviewAngle {
  const normalized = id.toLowerCase()
  if (normalized.includes('cost') || normalized.includes('util')) return 'cost'
  if (normalized.includes('observability') || normalized.includes('logging')) return 'security'
  if (normalized.includes('inventory') || normalized.includes('iac')) return 'drift'
  if (normalized.includes('compliance') || normalized.includes('tag')) return 'compliance'
  return 'compliance'
}

function isReviewableResource(node: PersistedNode): boolean {
  const props = propsOf(node)
  const resourceType = resourceTypeOf(node)
  if (['account', 'region', 'geographic_region', 'availability_zone'].includes(resourceType)) {
    return false
  }
  if (
    stringProp(props, ['resourceId', 'arn', 'providerResourceId']) ||
    stringProp(props, ['resourceType', 'managedService', '_manifestId', 'manifestId'])
  ) {
    return true
  }
  if (['artifact', 'data-store', 'actor', 'resource-group'].includes(node.type)) {
    return true
  }
  if (node.type === 'policy') {
    return Boolean(stringProp(props, ['hostingProvider', 'accountId', 'vpcId', 'subnetId']))
  }
  if (node.type === 'place') {
    return Boolean(stringProp(props, ['hostingProvider', 'accountId', 'vpcId', 'subnetId']))
  }
  return false
}

function propsOf(node: PersistedNode): Record<string, unknown> {
  return node.props && typeof node.props === 'object' && !Array.isArray(node.props)
    ? node.props as Record<string, unknown>
    : {}
}

function resourceTypeOf(node: PersistedNode): string {
  const props = propsOf(node)
  return (
    stringProp(props, ['resourceType', 'managedService', '_manifestId', 'manifestId']) ??
    node.type
  ).toLowerCase()
}

function publicExposureLevel(node: PersistedNode): 'none' | 'restricted' | 'open' | undefined {
  const props = propsOf(node)
  const value = props.publicExposure ?? nestedProp(props, ['access', 'publicExposure'])
  if (value === true) return 'open'
  if (value === false) return 'none'
  if (typeof value !== 'string') return undefined
  const normalized = value.toLowerCase()
  if (normalized === 'open' || normalized === 'public' || normalized === 'internet') return 'open'
  if (normalized === 'restricted') return 'restricted'
  if (normalized === 'none' || normalized === 'private') return 'none'
  return undefined
}

function encryptionAtRest(node: PersistedNode): boolean | undefined {
  const props = propsOf(node)
  const direct = boolProp(props.encryptedAtRest)
  if (typeof direct === 'boolean') return direct
  return boolProp(nestedProp(props, ['encryption', 'atRest']))
}

function isDataStore(node: PersistedNode): boolean {
  const type = `${node.type} ${resourceTypeOf(node)}`
  return [
    'data-store',
    'database',
    'db',
    'rds',
    'aurora',
    'sql',
    'postgres',
    'mysql',
    'dynamodb',
    'table',
    'bucket',
    'object_storage',
    'blob',
    'gcs',
    'warehouse',
    'redshift',
    'bigquery',
  ].some((term) => type.includes(term))
}

function monthlySpend(node: PersistedNode): number {
  const props = propsOf(node)
  return (
    numberProp(props.monthlySpend) ??
    numberProp(props.monthlyCostUsd) ??
    numberProp(props.monthlyCost) ??
    numberProp(nestedProp(props, ['cost', 'monthly'])) ??
    0
  )
}

function utilizationPct(node: PersistedNode): number | null {
  const props = propsOf(node)
  const value =
    numberProp(props.utilizationPct) ??
    numberProp(props.utilization) ??
    numberProp(nestedProp(props, ['metrics', 'utilizationPct'])) ??
    numberProp(nestedProp(props, ['metrics', 'utilization']))
  if (value == null) return null
  return value <= 1 ? value * 100 : value
}

function isIdle(node: PersistedNode): boolean {
  const props = propsOf(node)
  const state = stringProp(props, ['state', 'status', 'lifecycleState'])?.toLowerCase()
  if (state && ['stopped', 'terminated', 'idle', 'unused'].includes(state)) return true
  const utilization = utilizationPct(node)
  return utilization != null && utilization <= 5
}

function isStale(node: PersistedNode): boolean {
  const props = propsOf(node)
  const stamp = stringProp(props, ['lastTouched', 'lastModified', 'updatedAt', 'createdAt'])
  if (!stamp) return false
  const millis = Date.parse(stamp)
  if (!Number.isFinite(millis)) return false
  return Date.now() - millis > 90 * 24 * 60 * 60 * 1000
}

function isManualOrUnmanaged(node: PersistedNode): boolean {
  const props = propsOf(node)
  if (boolProp(props.iacManaged) === false) return true
  if (boolProp(props.createdOutsideIac) === true) return true
  if (boolProp(props.manual) === true || boolProp(props.isManual) === true) return true
  const drift = stringProp(props, ['driftStatus', 'iacStatus', 'governanceStatus'])?.toLowerCase()
  if (drift && ['drifted', 'manual', 'unmanaged', 'outside-iac', 'outside_iac'].includes(drift)) return true
  const source = stringProp(props, ['sourceKind', 'managedBy', 'changeSource'])?.toLowerCase()
  return !!source && ['manual', 'console', 'clickops', 'outside-iac', 'outside_iac'].includes(source)
}

function hasOwner(node: PersistedNode): boolean {
  const props = propsOf(node)
  if (stringProp(props, ['owner', 'team', 'serviceOwner', 'businessOwner', 'workload', 'application'])) {
    return true
  }
  const tags = tagsOf(node)
  return Boolean(tags.owner || tags.Owner || tags.team || tags.Team || tags.Workload || tags.workload)
}

function hasCostCenter(node: PersistedNode): boolean {
  const props = propsOf(node)
  if (stringProp(props, ['costCenter', 'cost_centre', 'costCentre'])) return true
  const tags = tagsOf(node)
  return Boolean(tags.costCenter || tags.CostCenter || tags.cost_centre || tags.CostCentre)
}

function hasConfigViolation(node: PersistedNode): boolean {
  const props = propsOf(node)
  const complianceType = stringProp(props, ['complianceType'])?.toUpperCase()
  if (complianceType && complianceType !== 'COMPLIANT' && complianceType !== 'NOT_APPLICABLE') {
    return true
  }
  const list = props.configRuleList
  return Array.isArray(list) && list.some((entry) => {
    if (!entry || typeof entry !== 'object') return false
    const compliance =
      (entry as Record<string, unknown>).ComplianceType ??
      (entry as Record<string, unknown>).complianceType
    return typeof compliance === 'string' &&
      compliance !== 'COMPLIANT' &&
      compliance !== 'NOT_APPLICABLE'
  })
}

function criticalConfigViolation(node: PersistedNode): boolean {
  const props = propsOf(node)
  const list = props.configRuleList
  if (!Array.isArray(list)) return false
  return list.some((entry) => {
    if (!entry || typeof entry !== 'object') return false
    const ruleName = String(
      (entry as Record<string, unknown>).ConfigRuleName ??
        (entry as Record<string, unknown>).configRuleName ??
        '',
    ).toLowerCase()
    return ['encrypt', 'public', 'iam', 'root', 'admin', 'mfa'].some((hint) => ruleName.includes(hint))
  })
}

function tagsOf(node: PersistedNode): Record<string, unknown> {
  const tags = propsOf(node).tags
  return tags && typeof tags === 'object' && !Array.isArray(tags)
    ? tags as Record<string, unknown>
    : {}
}

function stringProp(props: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = props[key]
    if (typeof value === 'string' && value.trim().length > 0) return value.trim()
  }
  return null
}

function nestedProp(root: Record<string, unknown>, path: readonly string[]): unknown {
  let current: unknown = root
  for (const key of path) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

function boolProp(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true
    if (value.toLowerCase() === 'false') return false
  }
  return undefined
}

function numberProp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function arrayOfStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : []
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function plural(count: number): string {
  return count === 1 ? '' : 's'
}

function dedupeById(items: NetworkReviewItem[]): NetworkReviewItem[] {
  const seen = new Set<string>()
  const unique: NetworkReviewItem[] = []
  for (const item of items) {
    if (seen.has(item.id)) continue
    seen.add(item.id)
    unique.push(item)
  }
  return unique
}
